import React, { useEffect, useState } from 'react';
import { host } from '../script/variables';
import '../style/AdminDocumentPreview.css';

const DOC_LABELS = {
  aadhaar_front: 'Aadhaar (Front)',
  aadhaar_back:  'Aadhaar (Back)',
  pan_card:      'PAN Card',
  fssai:         'FSSAI License',
  gst:           'GST Certificate',
};

const matchesTab = (status, tabKey) => {
  if (tabKey === 'partial') return status === 'partially_verified';
  return status === tabKey;
};

const AdminDocumentPreview = ({ docId, currentTab, onClose, onReviewed, onToast, onStatusChangedAway }) => {
  const [doc,         setDoc]         = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [actingKey,   setActingKey]   = useState(null);
  const [rejectingKey,setRejectingKey]= useState(null);
  const [reason,      setReason]      = useState('');
  const [bulkActing,  setBulkActing]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${host}/admin/documents/${docId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load document');
        const data = await res.json();
        if (!cancelled) setDoc(data.document);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [docId]);

  const applyUpdate = (updatedDoc) => {
    setDoc(updatedDoc);
    onReviewed?.(updatedDoc);
    if (currentTab !== 'all' && !matchesTab(updatedDoc.verificationStatus, currentTab)) {
      onStatusChangedAway?.();
    }
  };

  const reviewFile = async (docKey, action, rejectReason = '') => {
    setActingKey(docKey);
    try {
      const res = await fetch(`${host}/admin/documents/${docId}/files/${docKey}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason: rejectReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed');
      applyUpdate(data.document);
      onToast?.(`File ${action === 'verify' ? 'verified' : 'rejected'}`);
      setRejectingKey(null);
      setReason('');
    } catch (e) {
      onToast?.(e.message, 'error');
    } finally {
      setActingKey(null);
    }
  };

  const reviewAll = async (action) => {
    setBulkActing(true);
    try {
      const res = await fetch(`${host}/admin/documents/${docId}/review-all`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason: action === 'reject' ? 'Bulk rejected by admin' : '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed');
      applyUpdate(data.document);
      onToast?.(`All files ${action === 'verify' ? 'verified' : 'rejected'}`);
    } catch (e) {
      onToast?.(e.message, 'error');
    } finally {
      setBulkActing(false);
    }
  };

  return (
    <div className="adp-overlay" onClick={onClose}>
      <div className="adp-drawer" onClick={e => e.stopPropagation()}>
        <button className="adp-close" onClick={onClose} aria-label="Close">✕</button>

        {loading && <div className="adp-loading">Loading document…</div>}
        {error && <div className="adp-error">{error}</div>}

        {doc && (
          <>
            <div className="adp-header">
              <h2>
                {doc.partner?.displayName
                  || [doc.partner?.firstName, doc.partner?.lastName].filter(Boolean).join(' ')
                  || 'Unknown Owner'}
              </h2>
              <p className="adp-sub">
                {doc.restaurant?.name ? `${doc.restaurant.name} · ` : ''}
                {doc.category} documents
              </p>
              <div className="adp-contact">
                <span>{doc.partner?.phone || '—'}</span>
                <span className="adp-dot">·</span>
                <span>{doc.partner?.email || '—'}</span>
              </div>

              <div className="adp-bulk">
                <button className="adp-btn adp-btn--verify" disabled={bulkActing} onClick={() => reviewAll('verify')}>
                  {bulkActing ? '...' : 'Verify All'}
                </button>
                <button className="adp-btn adp-btn--reject" disabled={bulkActing} onClick={() => reviewAll('reject')}>
                  {bulkActing ? '...' : 'Reject All'}
                </button>
              </div>
            </div>

            <div className="adp-files">
              {Object.entries(doc.files || {}).map(([key, file]) => (
                <div className="adp-file" key={key}>
                  <div className="adp-file__preview">
                    {/\.(jpe?g|png|gif|webp)$/i.test(file.originalName) ? (
                      <img src={file.url} alt={file.originalName} />
                    ) : (
                      <a href={file.url} target="_blank" rel="noreferrer" className="adp-file__pdf">
                        📄 View File
                      </a>
                    )}
                  </div>

                  <div className="adp-file__info">
                    <div className="adp-file__name">
                      {DOC_LABELS[key] || file.label || key}
                      <span className={`adp-status adp-status--${file.status}`}>{file.status}</span>
                    </div>
                    <div className="adp-file__meta">
                      {file.originalName} · {(file.sizeBytes / 1024).toFixed(0)} KB · uploaded {new Date(file.uploadedAt).toLocaleDateString()}
                    </div>
                    {file.status === 'rejected' && file.rejectionReason && (
                      <div className="adp-file__reason">Reason: {file.rejectionReason}</div>
                    )}

                    {rejectingKey === key ? (
                      <div className="adp-reject-form">
                        <textarea
                          placeholder="Reason for rejection…"
                          value={reason}
                          onChange={e => setReason(e.target.value)}
                        />
                        <div className="adp-reject-actions">
                          <button
                            className="adp-btn adp-btn--reject"
                            disabled={!reason.trim() || actingKey === key}
                            onClick={() => reviewFile(key, 'reject', reason)}
                          >
                            {actingKey === key ? '...' : 'Confirm Reject'}
                          </button>
                          <button className="adp-btn adp-btn--ghost" onClick={() => { setRejectingKey(null); setReason(''); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="adp-file__actions">
                        <button
                          className="adp-btn adp-btn--verify"
                          disabled={actingKey === key || file.status === 'verified'}
                          onClick={() => reviewFile(key, 'verify')}
                        >
                          {actingKey === key ? '...' : 'Verify'}
                        </button>
                        <button
                          className="adp-btn adp-btn--reject"
                          disabled={actingKey === key || file.status === 'rejected'}
                          onClick={() => setRejectingKey(key)}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {Object.keys(doc.files || {}).length === 0 && (
                <p className="adp-empty">No files uploaded yet.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDocumentPreview;